import { Box } from '@mui/material';
import {
  AutocompleteArrayInput,
  BooleanField,
  DatagridConfigurable,
  ExportButton,
  FilterButton,
  FunctionField,
  Loading,
  NullableBooleanInput,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  TextArrayInput,
  TextField,
  useCanAccess,
} from 'react-admin';
import { ClickResource } from '../../Entities/Click';
import { createFilterQuery, DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';
import { ReactElement } from 'react';
import TzDateField from '../../Components/TzDate/TzDateField';
import TzDateInput from '../../Components/TzDate/TzDateInput';
import DetachedListPage from '../../Components/DetachedListPage';
import { DateTime } from 'luxon';
import { ExportAllButton } from './ExportAll';

const oneWeekAgo = DateTime.now()
  .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  .minus({ weeks: 1 })
  .toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");

const wrappedCellSx = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  verticalAlign: 'top',
};
const narrowDatagridSx = {
  tableLayout: 'fixed',
  '& .MuiTableCell-paddingCheckbox': {
    width: '2.5rem',
  },
  '& .column-id': {
    width: '5rem',
  },
  '& .column-attributes\\.client_ip': {
    width: '6.5rem',
  },
  '& .column-attributes\\.earnings': {
    width: '4.5rem',
  },
  '& .column-attributes\\.enhanced_partner_id': {
    width: '4.5rem',
  },
  '& .column-attributes\\.is_over_budget_click': {
    width: '4.5rem',
  },
  '& .column-attributes\\.referrer': {
    width: '7.5rem',
    ...wrappedCellSx,
  },
  '& .column-attributes\\.request_url': {
    width: '8rem',
    ...wrappedCellSx,
  },
  '& .column-attributes\\.timestamp': {
    width: '7rem',
    ...wrappedCellSx,
  },
  '& .column-attributes\\.user_agent': {
    width: '9rem',
    ...wrappedCellSx,
  },
  '& .column-relationships\\.category\\.data\\.id': {
    width: '6rem',
    ...wrappedCellSx,
  },
  '& .column-relationships\\.merchant\\.data\\.id': {
    width: '6rem',
    ...wrappedCellSx,
  },
  '& .column-relationships\\.offer\\.data\\.id': {
    width: '7rem',
    ...wrappedCellSx,
  },
  '& .column-relationships\\.product\\.data\\.id': {
    width: '7rem',
    ...wrappedCellSx,
  },
};

const filters = (
  canListCategories: boolean,
  canListMerchants: boolean,
  canListOffers: boolean,
  canListProducts: boolean,
): ReactElement[] =>
  [
    <TzDateInput
      key="filter.date_gte"
      label="Click Timestamp After"
      source="greaterOrEqual.timestamp"
      ignoreTimezoneInfo
      alwaysOn
    />,
    <TzDateInput
      key="filter.date_lte"
      label="Click Timestamp Before"
      source="lessOrEqual.timestamp"
      ignoreTimezoneInfo
      alwaysOn
    />,
    <NumberInput key="filter.id" label="ID" source="equals.id" />,
    <TextArrayInput key="filter.client_ip" label="Client IP" source="contains.client_ip" />,
    <TextArrayInput
      key="filter.refname_contains"
      label="Refname Contains"
      source="or.contains.refname"
    />,
    <TextArrayInput
      key="filter.refname_not_contain"
      label="Refname Does Not Contain"
      source="not.contains.refname"
    />,
    <NumberInput key="filter.earnings_lt" label="Earnings Less Than" source="lessThan.earnings" />,
    <NumberInput
      key="filter.earnings_gt"
      label="Earnings Greater Than"
      source="greaterThan.earnings"
    />,
    <TextArrayInput
      key="filter.enhanced_partner_id"
      label="Enhanced Partner IDs"
      source="equals.enhanced_partner_id"
    />,
    <TextArrayInput
      key="filter.exclude_enhanced_partner_id"
      label="Exclude Enhanced Partner IDs"
      source="not.equals.enhanced_partner_id"
    />,
    <NullableBooleanInput
      key="filter.is_over_budget_click"
      label="Is Click Over Budget?"
      source="equals.is_over_budget_click"
    />,
    <NullableBooleanInput
      key="filter.has_referrer"
      label="Has Referrer?"
      source="not.isNull.referrer"
    />,
    <TextArrayInput
      key="filter.referrer_contains"
      label="Referrer Contains"
      source="contains.referrer"
    />,
    <TextArrayInput
      key="filter.referrer_not_contain"
      label="Referrer Does Not Contain"
      source="not.contains.referrer"
    />,
    <NullableBooleanInput
      key="filter.has_request_url"
      label="Has Request URL?"
      source="not.isNull.request_url"
    />,
    <TextArrayInput
      key="filter.request_url_contains"
      label="Request URL Contains"
      source="contains.request_url"
    />,
    <TextArrayInput
      key="filter.request_url_not_contain"
      label="Request URL Does Not Contain"
      source="not.contains.request_url"
    />,
    <TextArrayInput
      key="filter.user_agent_contains"
      label="User Agent Contains"
      source="contains.user_agent"
    />,
    <TextArrayInput
      key="filter.user_agent_equals"
      label="User Agent Equals"
      source="equals.user_agent"
    />,
    <TextArrayInput
      key="filter.user_agent_not_contain"
      label="User Agent Does Not Contain"
      source="not.contains.user_agent"
    />,
    canListCategories && (
      <NullableBooleanInput
        key="filter.category_null"
        label="Has Category?"
        source="not.isNull.category"
      />
    ),
    canListCategories && (
      <ReferenceArrayInput
        label="Categories"
        key="filter.category.id"
        source="equals.category.id"
        reference="categories"
      >
        <AutocompleteArrayInput
          label="Categories"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListCategories && (
      <ReferenceArrayInput
        label="Exclude Categories"
        key="filter.not.category"
        source="not.equals.category.id"
        reference="categories"
      >
        <AutocompleteArrayInput
          label="Exclude Categories"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListMerchants && (
      <NullableBooleanInput
        key="filter.merchant_null"
        label="Has Merchant?"
        source="not.isNull.merchant"
      />
    ),
    canListMerchants && (
      <ReferenceArrayInput
        label="Merchants"
        key="filter.merchant.id"
        source="equals.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Merchants"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    canListMerchants && (
      <ReferenceArrayInput
        label="Exclude Merchants"
        key="filter.not.merchant"
        source="not.equals.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Exclude Merchants"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    canListOffers && (
      <NullableBooleanInput key="filter.offer_null" label="Has Offer?" source="not.isNull.offer" />
    ),
    canListOffers && (
      <ReferenceArrayInput
        label="Offers"
        key="filter.offer.id"
        source="equals.offer.id"
        reference="offers"
      >
        <AutocompleteArrayInput
          label="Offers"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListOffers && (
      <ReferenceArrayInput
        label="Exclude Offers"
        key="filter.not.offer"
        source="not.equals.offer.id"
        reference="offers"
      >
        <AutocompleteArrayInput
          label="Exclude Offers"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListProducts && (
      <NullableBooleanInput
        key="filter.product_null"
        label="Has Product?"
        source="not.isNull.product"
      />
    ),
    canListProducts && (
      <ReferenceArrayInput
        label="Products"
        key="filter.product.id"
        source="equals.product.id"
        reference="products"
      >
        <AutocompleteArrayInput
          label="Products"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListProducts && (
      <ReferenceArrayInput
        label="Exclude Products"
        key="filter.not.product"
        source="not.equals.product.id"
        reference="products"
      >
        <AutocompleteArrayInput
          label="Exclude Products"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
  ].filter((value) => typeof value !== 'boolean');

export default function ClickList(): ReactElement {
  const categoryAccess = useCanAccess({ resource: 'categories', action: 'list' });
  const merchantAccess = useCanAccess({ resource: 'merchants', action: 'list' });
  const offerAccess = useCanAccess({ resource: 'offers', action: 'list' });
  const productAccess = useCanAccess({ resource: 'products', action: 'list' });

  const isRelatedAccessPending =
    categoryAccess.isPending ||
    merchantAccess.isPending ||
    offerAccess.isPending ||
    productAccess.isPending;

  if (isRelatedAccessPending) {
    return <Loading />;
  }

  const canListCategories = categoryAccess.canAccess ?? false;
  const canListMerchants = merchantAccess.canAccess ?? false;
  const canListOffers = offerAccess.canAccess ?? false;
  const canListProducts = productAccess.canAccess ?? false;
  const renderWrappedValue = (value?: string | null): ReactElement => (
    <Box
      component="span"
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
    >
      {value ?? ''}
    </Box>
  );
  const listFilters = filters(canListCategories, canListMerchants, canListOffers, canListProducts);

  const prefetch: string[] = [];
  if (canListCategories) prefetch.push('category');
  if (canListMerchants) prefetch.push('merchant');
  if (canListOffers) prefetch.push('offer');
  if (canListProducts) prefetch.push('product');

  return (
    <DetachedListPage
      actions={
        <>
          <FilterButton filters={listFilters} />
          <ExportButton />
          <ExportAllButton />
        </>
      }
      filters={listFilters}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      filterDefaultValues={{ greaterOrEqual: { timestamp: oneWeekAgo } }}
      queryOptions={{ meta: { prefetch } }}
      sort={{ order: 'DESC', field: 'id' }}
    >
      <DatagridConfigurable
        omit={['attributes.enhanced_partner_id', 'relationships.offer.data.id']}
        sx={narrowDatagridSx}
      >
        <TextField label="ID" source="id" />
        <TextField label="Client IP" source="attributes.client_ip" />
        <NumberField label="Earnings (AUD cents)" source="attributes.earnings" />
        <TextField label="Enhanced Partner ID" source="attributes.enhanced_partner_id" />
        <BooleanField label="Over Budget Click?" source="attributes.is_over_budget_click" />
        <FunctionField
          label="Referrer"
          source="attributes.referrer"
          render={(record: ClickResource) => renderWrappedValue(record.attributes.referrer)}
        />
        <FunctionField
          label="Request URL"
          source="attributes.request_url"
          render={(record: ClickResource) => renderWrappedValue(record.attributes.request_url)}
        />
        <TzDateField showTime label="Timestamp" source="attributes.timestamp" />
        <FunctionField
          label="User Agent"
          source="attributes.user_agent"
          render={(record: ClickResource) => renderWrappedValue(record.attributes.user_agent)}
        />
        {canListCategories && (
          <ReferenceField
            label="Category"
            source="relationships.category.data.id"
            emptyText="Record missing"
            reference="categories"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        )}
        {canListMerchants && (
          <ReferenceField
            label="Merchant"
            source="relationships.merchant.data.id"
            emptyText="Record missing"
            reference="merchants"
          >
            <TextField source="attributes.merchant_name" />
          </ReferenceField>
        )}
        {canListOffers && (
          <ReferenceField
            label="Offer"
            source="relationships.offer.data.id"
            emptyText="Record missing"
            reference="offers"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        )}
        {canListProducts && (
          <ReferenceField
            label="Product"
            source="relationships.product.data.id"
            emptyText="Record missing"
            reference="products"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        )}
      </DatagridConfigurable>
    </DetachedListPage>
  );
}
